import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio, Swords, CheckCircle2, Clock } from "lucide-react";
import { api } from "../lib/api";
import { Page, PageTitle, Loader, Empty, RowsSkeleton } from "../components/UI";
import { MatchStatusBadge, ExhibitionBadge } from "../components/Arena";
import { ClubCrest } from "../components/ClubCrest";
import { useEventStream } from "../lib/sse";

export default function LivePage() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/live").then(({ data }) => setData(data)).catch(() => setData({ in_submission: [], in_progress: [], recently_finished: [] }));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEventStream("/live/stream", () => load());

  if (!data) return <RowsSkeleton rows={6} />;

  const liveCount = (data.in_submission?.length || 0) + (data.in_progress?.length || 0);

  return (
    <Page>
      <PageTitle
        eyebrow="Spectator Mode"
        title="Live Center"
        subtitle="Follow every ArenaBook team match in real time — open submissions, duels in progress and the latest final results."
        right={
          <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0f0f0f] border border-[#2b2512]">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span data-testid="live-count" className="font-mono text-sm text-slate-200">{liveCount} live now</span>
          </div>
        }
      />

      {data.featured_duel && (
        <Link to={`/duel/${data.featured_duel.id}`} data-testid="live-featured-duel"
          className="block mb-8 rounded-2xl border border-[#E5B842]/40 bg-gradient-to-r from-[#1a1608] to-[#070707] p-5 hover:border-[#E5B842]/70"
          style={{ transition: "border-color 0.2s ease" }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E5B842] font-mono flex items-center gap-2">
                <Swords className="w-3.5 h-3.5" /> Featured Duel · {data.featured_duel.label}
                <span className="px-2 py-0.5 rounded-full bg-fuchsia-950/60 text-fuchsia-300 border border-fuchsia-500/40">SHOWMATCH</span>
              </div>
              <div className="font-heading text-xl font-black text-slate-50 mt-1">
                {data.featured_duel.home_player_id ? "Live showmatch in the Duel Theatre" : ""}
              </div>
              <div className="text-xs text-slate-500">Status: {data.featured_duel.status} · non-rated exhibition</div>
            </div>
            <span className="px-4 py-2 rounded-lg bg-[#E5B842] text-[#000000] text-sm font-bold">Watch live →</span>
          </div>
        </Link>
      )}

      <Section title="In Submission" Icon={Clock} tint="text-emerald-400" matches={data.in_submission} testid="live-submission" empty="No matches accepting coupons right now." />
      <Section title="Duels In Progress" Icon={Radio} tint="text-[#E5B31E]" matches={data.in_progress} testid="live-inprogress" empty="No matches currently resolving." showScore />
      <Section title="Recently Finished" Icon={CheckCircle2} tint="text-[#E5B842]" matches={data.recently_finished} testid="live-finished" empty="No completed matches yet." showScore final />
    </Page>
  );
}

function Section({ title, Icon, tint, matches, testid, empty, showScore, final }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">
        <Icon className={`w-5 h-5 ${tint}`} /> {title}
        <span className="text-slate-600 text-lg">· {matches?.length || 0}</span>
      </h2>
      {!matches || matches.length === 0 ? (
        <Empty label={empty} />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {matches.map((m) => <LiveCard key={m.id} m={m} showScore={showScore} final={final} />)}
        </div>
      )}
    </section>
  );
}

function LiveCard({ m, showScore, final }) {
  const s = m.live || {};
  const clinch = s.clinched_side;
  return (
    <Link
      to={`/match/${m.id}`}
      data-testid={`live-card-${m.public_match_id}`}
      className="block rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/40"
      style={{ transition: "border-color 0.15s ease" }}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="font-mono text-[11px] text-[#E5B842] tracking-wider truncate">{m.public_match_id}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {m.is_exhibition && <ExhibitionBadge />}
          {m.is_continental_fixture && m.tier && (
            <span data-testid="live-tier-badge" className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase tracking-wider border bg-[#2a2410]/60 text-[#E5B842] border-[#E5B842]/40">
              {m.tier === 1 ? "Champions" : m.tier === 2 ? "Euro Cup" : "Challenge"}
            </span>
          )}
          <MatchStatusBadge status={m.status} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <TeamMini club={m.home_club} />
        <div className="flex flex-col items-center shrink-0 min-w-[72px]">
          {showScore ? (
            <div className="font-heading text-3xl font-black text-[#E5B842] tabular-nums leading-none">
              {s.home_team_points ?? 0}<span className="text-slate-600 px-1">–</span>{s.away_team_points ?? 0}
            </div>
          ) : (
            <Swords className="w-6 h-6 text-slate-500" />
          )}
          <span className="text-[10px] font-mono text-slate-500 mt-1">
            {final ? "FINAL" : `${s.resolved_count ?? 0}/${s.total_duels ?? 11} duels`}
          </span>
        </div>
        <TeamMini club={m.away_club} align="right" />
      </div>
      {!final && clinch && (
        <div data-testid="live-clinch" className="mt-3 text-center text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-400">
          {clinch === "HOME" ? m.home_club?.short_name || m.home_club?.name : m.away_club?.short_name || m.away_club?.name} · winner confirmed
        </div>
      )}
    </Link>
  );
}

function TeamMini({ club, align }) {
  return (
    <div className={`flex items-center gap-2 flex-1 min-w-0 ${align === "right" ? "flex-row-reverse" : ""}`}>
      <ClubCrest club={club} size={36} />
      <div className={`min-w-0 ${align === "right" ? "text-right" : ""}`}>
        <div className="font-heading font-bold uppercase tracking-tight text-slate-100 text-sm truncate">{club?.short_name || club?.name}</div>
      </div>
    </div>
  );
}
